<template>
  <div class="profile-container">
    <div class="profile-header">
      <h2 class="subtitle">{{ $t('navigation.profile') }}</h2>
      <p class="profile-subtitle">{{ $t('userProfile.manageProfile') }}</p>
    </div>
    
    <!-- User Information Section -->
    <div class="profile-section" v-if="user">
      <div class="section-header">
        <h3>👤 {{ $t('userProfile.basicInfo') }}</h3>
      </div>
      <div class="info-grid">
        <div class="info-item">
          <label>{{ $t('userProfile.firstName') }}:</label>
          <span>{{ user.first_name }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('userProfile.lastName') }}:</label>
          <span>{{ user.last_name }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('auth.username') }}:</label>
          <span>{{ user.username }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('userProfile.email') }}:</label>
          <span>{{ user.email }}</span>
        </div>
        <div class="info-item" v-if="user.phone">
          <label>{{ $t('userProfile.phone') }}:</label>
          <span>{{ user.phone }}</span>
        </div>
        <div class="info-item">
          <label>{{ $t('userProfile.role') }}:</label>
          <span class="role-badge" :class="user.role">{{ $t('roles.' + user.role) }}</span>
        </div>
      </div>
    </div>

    <!-- Employment Information -->
    <div class="profile-section" v-if="user">
      <div class="section-header">
        <h3>💼 {{ $t('userProfile.employmentInfo') }}</h3>
      </div>
      
      <!-- Full-time worker -->
      <div class="info-grid" v-if="!isPartTime">
        <div class="info-item" v-if="user.payroll?.monthly_salary || user.payroll?.hourly_salary">
          <label>{{ $t('userProfile.monthlySalary') }} ({{ $t('userProfile.nominal') }}):</label>
          <span class="salary">{{ formatCurrency(user.payroll.monthly_salary) }}</span>
        </div>
        <div class="info-item" v-if="user.employment_date">
          <label>{{ $t('userManagement.employmentDate') }}:</label>
          <span>{{ formatDate(user.employment_date) }}</span>
        </div>
      </div>
      
      <!-- Part-time worker -->
      <div class="info-grid" v-if="isPartTime">
        <div class="info-item" v-if="user.payroll?.monthly_salary || user.payroll?.hourly_salary">
          <label>{{ $t('userProfile.hourlySalary') }} ({{ $t('userProfile.nominal') }}):</label>
          <span class="salary">{{ parseFloat(user.payroll.hourly_salary || 0).toFixed(2) }} SEK/h</span>
        </div>
        <div class="info-item" v-if="user.payroll?.monthly_salary || user.payroll?.hourly_salary">
          <label>{{ $t('userProfile.monthlySalary') }} ({{ $t('userProfile.nominal') }}):</label>
          <span class="salary">{{ formatCurrency(user.payroll.monthly_salary) }}</span>
        </div>
        <div class="info-item" v-if="user.payroll?.employment_hours_per_week">
          <label>{{ $t('userManagement.employmentHoursPerWeek') }}:</label>
          <span>{{ user.payroll.employment_hours_per_week }}h/{{ $t('weekdays.week').toLowerCase() }}</span>
        </div>
        <div class="info-item" v-if="user.employment_date">
          <label>{{ $t('userManagement.employmentDate') }}:</label>
          <span>{{ formatDate(user.employment_date) }}</span>
        </div>
        <div class="info-item" v-if="user.payroll?.employment_rate">
          <label>{{ $t('userManagement.employmentRate') }}:</label>
          <span>{{ user.payroll.employment_rate }}%</span>
        </div>
      </div>
        <div class="info-item" v-if="user.payroll && user.payroll.employment_type">
          <label>{{ $t('userProfile.employmentType') }}:</label>
          <span>{{ $t('userProfile.' + user.payroll.employment_type) }}</span>
        </div>
    </div>

    <!-- Current Month Statistics -->
    <div class="profile-section" v-if="user">
      <div class="section-header">
        <h3>📊 {{ $t('userProfile.monthlyStatistics') }}</h3>
        <div class="month-picker">
          <button @click="previousMonth" class="month-nav-btn">
            <span class="arrow">◀</span>
          </button>
          <span class="current-month">{{ formatMonthYear(selectedDate) }}</span>
          <button @click="nextMonth" class="month-nav-btn">
            <span class="arrow">▶</span>
          </button>
        </div>
      </div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon">⏰</div>
          <div class="stat-content">
            <div class="stat-label">{{ $t('userInfo.totalOB') }}</div>
            <div class="stat-value">{{ totalOBHours.toFixed(2) }}h</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">💰</div>
          <div class="stat-content">
            <div class="stat-label">{{ $t('userInfo.obHours') }}</div>
            <div class="stat-value">{{ formatCurrency(monthlyOBBonus) }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">💵</div>
          <div class="stat-content">
            <div class="stat-label">{{ $t('userManagement.grossSalary') }}</div>
            <div class="stat-value">{{ formatCurrency(grossSalary) }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">🏛️</div>
          <div class="stat-content">
            <div class="stat-label">{{ $t('userManagement.employeeTax') }}</div>
            <div class="stat-value">{{ formatCurrency(employeeTax) }}</div>
          </div>
        </div>
        <div class="stat-card highlight">
          <div class="stat-icon">✅</div>
          <div class="stat-content">
            <div class="stat-label">{{ $t('userProfile.netSalary') }}</div>
            <div class="stat-value">{{ formatCurrency(netSalary) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- OB Hours Breakdown -->
    <div class="profile-section" v-if="user && totalOBHours > 0">
      <div class="section-header">
        <h3>⏱️ {{ $t('userProfile.obBreakdown') }}</h3>
      </div>
      <div class="ob-breakdown">
        <div class="ob-item">
          <span class="ob-type">{{ $t('pdf.ob1') }} (1.3x)</span>
          <span class="ob-hours">{{ currentMonthOB.ob1_hours.toFixed(2) }}{{ $t('common.hours') }}</span>
        </div>
        <div class="ob-item">
          <span class="ob-type">{{ $t('pdf.ob2') }} (1.5x)</span>
          <span class="ob-hours">{{ currentMonthOB.ob2_hours.toFixed(2) }}{{ $t('common.hours') }}</span>
        </div>
        <div class="ob-item">
          <span class="ob-type">{{ $t('pdf.ob3') }} (2.0x)</span>
          <span class="ob-hours">{{ currentMonthOB.ob3_hours.toFixed(2) }}{{ $t('common.hours') }}</span>
        </div>
      </div>
    </div>

    <!-- Change Password Button and Modal -->
    <div class="profile-section">
      <button class="change-password-btn" @click="showPasswordModal = true">
        🔒 {{ $t('userProfile.changePassword') || 'Change Password' }}
      </button>
    </div>

    <!-- Loading and Error states -->
    <div v-if="!user && !error" class="loading">
      <div class="spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <div v-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="fetchUserData" class="retry-btn">{{ $t('common.retry') }}</button>
    </div>
  </div>
  <div v-if="showPasswordModal" class="modal-overlay" @click.self="showPasswordModal = false">
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ $t('userProfile.changePassword') || 'Change Password' }}</h3>
            <button class="modal-close" @click="showPasswordModal = false">&times;</button>
          </div>
          <form @submit.prevent="handleChangePassword" class="change-password-form">
            <div class="form-group">
              <label for="currentPassword">{{ $t('userProfile.currentPassword') || 'Current Password' }}</label>
              <input id="currentPassword" v-model="currentPassword" type="password" required />
            </div>
            <div class="form-group">
              <label for="newPassword">{{ $t('userProfile.newPassword') || 'New Password' }}</label>
              <input id="newPassword" v-model="newPassword" type="password" required />
            </div>
            <div class="form-group">
              <label for="confirmPassword">{{ $t('userProfile.confirmPassword') || 'Confirm New Password' }}</label>
              <input id="confirmPassword" v-model="confirmPassword" type="password" required />
            </div>
            <button type="submit" class="change-password-btn">{{ $t('userProfile.savePassword') || 'Change Password' }}</button>
            <div v-if="passwordMessage" :class="['password-message', passwordMessageType]">{{ passwordMessage }}</div>
          </form>
        </div>
      </div>
</template>

<script setup>
// Get OB coefficients from company settings or fallback to defaults
const obCoefficients = computed(() => {
  const company = user.value?.company || authStore.company
  // Debug: log the company object and OB fields
  if (company) {
    return {
      ob1: Number(company.overtime_rate1) ?? 0.3,
      ob2: Number(company.overtime_rate2) ?? 0.5,
      ob3: Number(company.overtime_rate3) ?? 1.0,
    }
  }
  return { ob1: 0.3, ob2: 0.5, ob3: 1.0 }
})
const showPasswordModal = ref(false)
import { ref, computed, watch, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { useShiftStore } from '../stores/shift.js'
import { usePayrollStore } from '../stores/payroll.js'
import { useI18n } from 'vue-i18n'

const { locale, t } = useI18n()
const user = ref(null)
const error = ref('')
const authStore = useAuthStore()
const shiftStore = useShiftStore()
const payrollStore = usePayrollStore()
const selectedDate = ref(new Date())
// Check if user is part-time worker
const isPartTime = computed(() => {
  const employmentRate = parseFloat(user.value?.payroll?.employment_rate || 100)
  return employmentRate < 100
})

// Use centralized payroll store for all salary calculations
const baseSalary = computed(() => {
  return user.value ? payrollStore.getUserBaseSalary(user.value.id) : 0
})

const grossSalary = computed(() => {
  return user.value ? payrollStore.getUserGrossSalary(user.value.id) : 0
})

const employeeTax = computed(() => {
  return user.value ? payrollStore.getUserEmployeeTax(user.value.id) : 0
})

const netSalary = computed(() => {
  return user.value ? payrollStore.getUserNetSalary(user.value.id) : 0
})

// Get OB data from payroll store
const currentMonthOB = computed(() => {
  if (!user.value) return { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 }
  
  const obData = payrollStore.monthlyOBHours[user.value.id] || {}
  return {
    ob1_hours: obData.ob1_hours || 0,
    ob2_hours: obData.ob2_hours || 0,
    ob3_hours: obData.ob3_hours || 0,
    total_hours: obData.total_hours || 0
  }
})

const totalOBHours = computed(() => {
  const ob = currentMonthOB.value
  const total = parseFloat(ob.ob1_hours || 0) + parseFloat(ob.ob2_hours || 0) + parseFloat(ob.ob3_hours || 0)
  return total || 0
})

const monthlyOBBonus = computed(() => {
  if (!user.value) return 0
  
  // Calculate OB bonus using the same logic as the payroll store
  const ob = currentMonthOB.value
  const hourlyRate = parseFloat(user.value.payroll?.hourly_salary || 0)
  
  if (hourlyRate === 0) return 0
  
  const ob1Bonus = parseFloat(ob.ob1_hours || 0) * hourlyRate * obCoefficients.value.ob1
  const ob2Bonus = parseFloat(ob.ob2_hours || 0) * hourlyRate * obCoefficients.value.ob2
  const ob3Bonus = parseFloat(ob.ob3_hours || 0) * hourlyRate * obCoefficients.value.ob3
  
  return ob1Bonus + ob2Bonus + ob3Bonus
})

// Old computed properties replaced by centralized payroll store above

// OLD FUNCTION - Tax calculation now handled by centralized payroll store
// async function calculateUserTax() {
//   - removed for brevity, tax calculation is now centralized in payroll store
// }

// 🚀 OPTIMIZATION: Cached currency formatter
const currencyFormatter = new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

function formatCurrency(amount) {
  return currencyFormatter.format(amount)
}

function formatDate(dateString) {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('sv-SE')
}

// 🚀 OPTIMIZATION: Efficient month navigation using payroll store
function previousMonth() {
  selectedDate.value = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth() - 1, 1)
  payrollStore.selectedMonth = selectedDate.value
  if (user.value) {
    payrollStore.loadMonthlyData([user.value])
  }
}

function nextMonth() {
  selectedDate.value = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth() + 1, 1)
  payrollStore.selectedMonth = selectedDate.value
  if (user.value) {
    payrollStore.loadMonthlyData([user.value])
  }
}

function formatMonthYear(date) {
  const localeCode = locale.value === 'sv' ? 'sv-SE' : 'en-US'
  return date.toLocaleDateString(localeCode, { year: 'numeric', month: 'long' })
}

// Watch for changes in gross salary (now handled by payroll store)
watch(grossSalary, () => {
  // Tax calculation is now handled by the payroll store
})

// Watch for changes from TimeReporting component
watch(() => shiftStore.currentDate, async (newDate) => {
  if (newDate && user.value) {
    selectedDate.value = new Date(newDate)
    // Reload all payroll data through centralized store
    await payrollStore.loadMonthlyData([user.value])
  }
}, { deep: true })

// Fetch user data on component mount
onMounted(async () => {
  // Sync with shiftStore if available
  if (shiftStore.currentDate) {
    selectedDate.value = new Date(shiftStore.currentDate)
  }
  
  await fetchUserData()
  
  // Load payroll data using centralized store
  if (user.value) {
    await payrollStore.loadMonthlyData([user.value])
  }
})

async function fetchUserData() {
  try {
    if (!authStore.isAuthenticated) {
      error.value = 'Du måste vara inloggad för att se profilen'
      return
    }

    // Fetch complete user data from API to ensure we have payroll info
    if (authStore.user?.id) {
      const response = await authStore.makeAuthenticatedRequest(
        `/api/users/${authStore.user.id}/`
      )
      if (response.ok) {
        user.value = await response.json()
        // If payroll is null, fetch it separately
        if (!user.value.payroll) {
          try {
            // Workers: call without parameters (backend filters automatically)
            // Managers: can use ?user={id} parameter to query specific user
            const payrollUrl = authStore.user.role === 'manager' 
              ? `/api/payroll/?user=${user.value.id}`
              : `/api/payroll/`
            const payrollResponse = await authStore.makeAuthenticatedRequest(payrollUrl)
            if (payrollResponse.ok) {
              const payrollData = await payrollResponse.json()
              // Check if response is array or single object
              if (Array.isArray(payrollData) && payrollData.length > 0) {
                user.value.payroll = payrollData[0]
              } else if (payrollData && !Array.isArray(payrollData)) {
                user.value.payroll = payrollData
              }
            }
          } catch (payrollError) {
          }
        }
        error.value = ''
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } else {
      error.value = 'Kunde inte ladda användardata'
    }
  } catch (e) {
    console.error('Error fetching user data:', e)
    error.value = 'Nätverksfel vid laddning av användardata'
  }
}

// OLD FUNCTION - OB hours loading now handled by centralized payroll store
// async function loadMonthlyOBHours() {
//   - removed for brevity, OB hours calculation is now centralized in payroll store
// }

// OLD FUNCTION - Assigned hours loading now handled by centralized payroll store
// async function loadMonthlyAssignedHours() {
//   - removed for brevity, assigned hours calculation is now centralized in payroll store
// }

// OLD FUNCTION - Sick leaves loading now handled by centralized payroll store
// async function loadMonthlySickLeaves() {
//   - removed for brevity, sick leave calculation is now centralized in payroll store
// }

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const passwordMessage = ref('')
const passwordMessageType = ref('')

async function handleChangePassword() {
  passwordMessage.value = ''
  passwordMessageType.value = ''
  if (newPassword.value !== confirmPassword.value) {
    passwordMessage.value = t('userProfile.passwordsDoNotMatch') || 'Passwords do not match.'
    passwordMessageType.value = 'error'
    return
  }
  try {
    const res = await authStore.makeAuthenticatedRequest(
      `/api/users/change_password/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          old_password: currentPassword.value,
          new_password: newPassword.value
        })
      }
    )
    if (res.ok) {
      passwordMessage.value = t('userProfile.passwordChanged') || 'Password changed successfully.'
      passwordMessageType.value = 'success'
      currentPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''
    } else {
      const data = await res.json().catch(() => ({}))
      if (data.detail === 'Old password is incorrect.') {
        passwordMessage.value = t('userProfile.oldPasswordIncorrect') || 'Old password is incorrect.'
      } else {
        passwordMessage.value = data.detail || t('userProfile.passwordChangeFailed') || 'Password change failed.'
      }
      passwordMessageType.value = 'error'
    }
  } catch (err) {
    passwordMessage.value = t('userProfile.passwordChangeFailed') || 'Password change failed.'
    passwordMessageType.value = 'error'
  }
}
</script>

<style scoped>
/* Modal Styles */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.modal-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: #888;
  cursor: pointer;
  line-height: 1;
  padding: 0 0.5rem;
  margin-left: 1rem;
}
.profile-container {
  max-width: 900px;
  margin: 2rem auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.profile-header {
  text-align: center;
  margin-bottom: 3rem;
}

.profile-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #0a2544;
  margin-bottom: 0.5rem;
}

.profile-subtitle {
  font-size: 1.1rem;
  color: #666;
  opacity: 0.8;
}

.profile-section {
  margin-bottom: 2.5rem;
  background: #f8f9fa;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.section-header {
  margin-bottom: 1.5rem;
  border-bottom: 2px solid rgba(33, 150, 243, 0.1);
  padding-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-header h3 {
  font-size: 1.4rem;
  font-weight: 600;
  color: #0a2544;
  margin: 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin: 1rem 0.5rem;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.info-item label {
  font-weight: 600;
  color: #0a2544;
  margin-right: 1rem;
}

.info-item span {
  color: #333;
  font-weight: 500;
}

.role-badge {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
}

.role-badge.manager {
  background: #f3e5f5;
  color: #7b1fa2;
}

.salary {
  color: #2e7d32;
  font-weight: 700;
  font-size: 1.1rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card.highlight {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
}

.stat-icon {
  font-size: 2rem;
  opacity: 0.8;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.8;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.ob-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ob-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.ob-type {
  font-weight: 600;
  color: #0a2544;
}

.ob-hours {
  color: #2196f3;
  font-weight: 700;
  font-size: 1.1rem;
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(33, 150, 243, 0.1);
  border-left: 4px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  text-align: center;
  padding: 2rem;
  background: #ffebee;
  border-radius: 12px;
  color: #c62828;
  margin: 1rem 0;
}

.retry-btn {
  background: #2196f3;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 1rem;
  transition: background-color 0.2s ease;
}

.retry-btn:hover {
  background: #1976d2;
}

.change-password-form {
  max-width: 400px;
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.change-password-form .form-group {
  margin-bottom: 1rem;
}

.change-password-form label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.change-password-form input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.change-password-btn {
  background: #2196f3;
  color: #fff;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
}

.change-password-btn:hover {
  background: #1769aa;
}

.password-message {
  margin-top: 0.75rem;
  font-weight: 500;
}

.password-message.success {
  color: #388e3c;
}

.password-message.error {
  color: #d32f2f;
}

@media screen and (max-width: 768px) {
  .profile-container {
    margin: 1rem;
    padding: 1rem;
  }
  
  .profile-header h2 {
    font-size: 2rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    padding: 1rem;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}

/* Month Picker Styles */
.month-picker {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.month-nav-btn {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.month-nav-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
}

.month-nav-btn:active {
  transform: translateY(0);
}

.month-nav-btn .arrow {
  font-size: 1.2rem;
  font-weight: bold;
}

.current-month {
  font-size: 1rem;
  font-weight: 600;
  color: #0a2544;
  min-width: 150px;
  text-align: center;
  text-transform: capitalize;
}

@media screen and (max-width: 768px) {
  .month-picker {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .current-month {
    min-width: 120px;
    font-size: 0.9rem;
  }
  
  .month-nav-btn {
    width: 32px;
    height: 32px;
  }
}
</style>